//@flow
import * as React from 'react';
import { translate, type TFunction } from 'react-i18next';
import theme from './FpTimelineRow.scss';
import classnames from 'classnames';

import Div from 'common/components/core/wrappers/Div';
import HandleOutsideClick from 'common/components/core/util/HandleOutsideClick';
import { KeyEventHandler } from 'common/components/core/util/KeyEventsHandler';
import ContextMenu from 'common/components/core/context_menu/ContextMenu';
import StopPropagation from 'common/components/core/StopPropagation/StopPropagation';

import type {
    EventGroupType,
    EventType
} from 'common/components/common/timeline/Timeline.types';
import type { VisitIndexReferenceType } from 'common/types/common.types';
import type { List } from 'immutable';
import { PermissionMatch, RoleOperationEnum } from 'common/modules/rbac';

const EnhancedContextMenu = HandleOutsideClick(KeyEventHandler(ContextMenu));

const EventsComponent = ({ theme, className, children }) => {
    return (
        <Div className={className}>
            <div className={theme.events}>{children}</div>
        </Div>
    );
};

const EventComponent = ({ style, className, event }) => {
    return (
        <Div style={style} className={className}>
            {event.get('label')}
        </Div>
    );
};

type Props = {
    eventGroup: EventGroupType,
    visibleTimeStart: number,
    visibleTimeEnd: number,
    scale: number,
    startPx: number,
    endPx: number,
    scaleFactor: number,
    enableContextMenu: boolean,
    isRowSelected: boolean,
    enableVisitTypeArrows: boolean,
    onMouseOver: (
        eventGroupId: string,
        eventId: string,
        sourceId: string,
        transactionType: string,
        tourType: string
    ) => void,
    onMouseOut: (
        eventGroupId: string,
        eventId: string,
        sourceId: string,
        transactionType: string
    ) => void,
    onSelect: (
        eventGroupId: string,
        eventId: string,
        sourceId: string,
        transactionType: string,
        tourType: string
    ) => void,
    onEventRemoveClick: (
        eventGroupId: string,
        eventId: string,
        sourceId: string,
        transactionType: string
    ) => void,
    getExtraContextMenuOptions?: List<Object>,
    onContextMenuOpen?: (
        x: number,
        y: number,
        eventGroupId: string,
        eventId: VisitIndexReferenceType
    ) => void,
    onContextMenuClose?: () => void,
    isFrozen: boolean,
    t: TFunction,
    className: string,
    renderScaleLinesInEvent: boolean
};

type State = {
    contextId: ?string
};

const factory = ({
    Event = EventComponent,
    Events = EventsComponent,
    ContextMenu = EnhancedContextMenu
}) => {
    class TimelineRow extends React.PureComponent<Props, State> {
        static defaultProps = {
            enableContextMenu: true
        };
        state: State = {};
        extraContainerRef = null;
        //scale default 1 pixel = 1 min
        calculateEventStyle(
            event: EventType,
            visibleTimeStart: number,
            scale: number = 60 * 1000,
            scaleFactor: number
        ) {
            const style = {};
            const effectiveScale = scale * (1 / scaleFactor);
            const eventStart = event.get('startTime');
            const eventEnd = event.get('endTime');
            const timeToStart = eventStart - visibleTimeStart;
            const pxToStart = timeToStart / effectiveScale;
            style.left = pxToStart;
            const timeSpan = eventEnd - eventStart;
            const pxWidth = timeSpan / effectiveScale;
            style.width = pxWidth;
            return style;
        }

        onMouseOver = (beatId: string, visitId: string) => {
            const { onMouseOver } = this.props;
            onMouseOver && onMouseOver(beatId, visitId);
        };

        onMouseOut = () => {
            const { onMouseOut } = this.props;
            onMouseOut && onMouseOut();
        };

        onSelect = (beatId: string, visitId: string) => {
            const { onSelect } = this.props;
            onSelect && onSelect(beatId, visitId);
        };

        onRightClick = (
            x: number,
            y: number,
            beatId: string,
            visitId: string
        ) => {
            const { onContextMenuOpen, status } = this.props;
            if (onContextMenuOpen && status !== 'FROZEN') {
                const contextId = onContextMenuOpen(x, y, beatId, visitId);

                this.setState({ contextId });
            }
        };

        getEventList = (events, hasEditPermission) => {
            const {
                visibleTimeStart,
                startPx,
                endPx,
                scale,
                scaleFactor,
                isFrozen,
                isRowSelected,
                enableVisitTypeArrows,
                ...others
            } = this.props;

            return events
                .map(event => {
                    let isEndReached = false;
                    const eventStyle = this.calculateEventStyle(
                        event,
                        visibleTimeStart,
                        scale,
                        scaleFactor
                    );
                    if (
                        eventStyle.left + eventStyle.width < startPx ||
                        eventStyle.left > endPx
                    )
                        if (event.left > endPx) {
                            isEndReached = true;
                        }
                    if (!isEndReached) {
                        return (
                            <Event
                                {...others}
                                isFrozen={isFrozen}
                                style={eventStyle}
                                key={`${event.get('beatId')}-${event.get(
                                    'eventId'
                                )}`}
                                isRowSelected={isRowSelected}
                                enableVisitTypeArrows={enableVisitTypeArrows}
                                className={theme.event}
                                event={event}
                                onContextMenu={
                                    hasEditPermission
                                        ? this.onRightClick
                                        : undefined
                                }
                                onMouseOver={this.onMouseOver}
                                onMouseOut={this.onMouseOut}
                                onSelect={this.onSelect}
                            />
                        );
                    }
                    return null;
                })
                .filter(event => {
                    return event;
                });
        };

        _getEventsWithPermissionCheck = hasPermission => {
            const { scaleLines, eventGroup } = this.props;
            const events = eventGroup.get('events');
            const rowColor = events.get(0).getIn(['color', 'code']);
            return (
                <Events
                    rowColor={rowColor}
                    theme={theme}
                    className={theme.eventsWrap}
                >
                    {scaleLines}
                    {this.getEventList(events, hasPermission)}
                </Events>
            );
        };

        closeContextMenu = () => {
            this.setState({ contextId: null }, () => {
                const { onContextMenuClose } = this.props;
                onContextMenuClose && onContextMenuClose();
            });
        };

        handleRemoveEvent = () => {
            const { contextMenu, onUnallocateVisit } = this.props;
            if (contextMenu) {
                const { beatId, visitId } = contextMenu;
                onUnallocateVisit(beatId, visitId);
            }
            this.closeContextMenu(contextMenu);
        };

        getExtraContextMenuOptions = () => {
            const { getExtraContextMenuOptions } = this.props;
            return getExtraContextMenuOptions?.() || [];
        };

        handleMoveBeatClick = () => {
            const { openMoveVisitDialog, contextMenu } = this.props;
            const { beatId, visitId } = contextMenu;
            openMoveVisitDialog(visitId, beatId);
            this.closeContextMenu();
        };

        renderContextMenu() {
            const { t, contextMenu } = this.props;
            const { contextId } = this.state;
            if (!contextMenu || contextMenu?.id !== contextId) {
                return [];
            }
            const items = [
                {
                    value: t('remove'),
                    onClick: this.handleRemoveEvent
                },
                {
                    value: t('move'),
                    onClick: this.handleMoveBeatClick
                }
            ];
            return (
                <ContextMenu
                    {...this.props}
                    onEscape={this.closeContextMenu}
                    getContextMenuPortalRef={this.getContextMenuPortalRef}
                    getExtraContainerRef={this.getExtraContainerRef}
                    x={contextMenu.x}
                    y={contextMenu.y}
                    closeContextMenu={this.closeContextMenu}
                    handleClickOutside={this.closeContextMenu}
                    getContainer={this.getContextMenuContainer}
                    getExtraContainer={this.getExtraContainer}
                    items={items}
                />
            );
        }

        getExtraContainerRef = ref => {
            this.extraContainerRef = ref;
        };

        getExtraContainer = () => {
            if (this.extraContainerRef) {
                return this.extraContainerRef;
            }
            return null;
        };

        getContextMenuPortalRef = ref => {
            this.contextMenuPortalRef = ref;
        };

        getContextMenuContainer = () => {
            return this.contextMenuPortalRef;
        };

        onToggle = () => {
            const { onToggle, eventGroup } = this.props;
            onToggle(eventGroup.get('tourId'), eventGroup.get('ganttType'));
        };

        stopPropagation = e => e.stopPropagation();

        render() {
            const { className, renderScaleLinesInEvent } = this.props;
            return (
                <div
                    className={classnames(theme.rowContainer, {
                        [className]: className,
                        [theme.liveViewTimelineRow]: renderScaleLinesInEvent
                    })}
                >
                    <PermissionMatch
                        operations={RoleOperationEnum.EDIT_PJP}
                        render={this._getEventsWithPermissionCheck}
                    />

                    {/* Stoping the mousedown to propagate to avoid triggering the drag on timeline */}
                    <StopPropagation eventTypes="onMouseDown">
                        {this.renderContextMenu()}
                    </StopPropagation>
                </div>
            );
        }
    }
    return translate('planner')(TimelineRow);
};
const DefaultTimelineRow = factory({});
export default DefaultTimelineRow;
export { DefaultTimelineRow as TimelineRow };
export { factory as FpTimelineRowFactory };
